import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationExtras } from '@angular/router';
import { UntypedFormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subscription } from 'rxjs';
import { debounceTime, filter, shareReplay, take } from 'rxjs/operators';

import { findProblemCountByFreeword, initializeProblemCountState, initializeSearchCondition } from 'src/app/actions/search.actions';
import { reportAppEvent } from 'src/app/actions/event-log.actions';
import { dispatchAppError, navigate } from 'src/app/actions/core.actions';
import { RootState } from 'src/app/reducers';
import { getSubject } from 'src/app/selectors/static-data.selectors';
import { getSignedInUser } from 'src/app/selectors/auth.selectors';
import { getMatchedProblemCount, getProblemCountSearching, getSearchCondition } from 'src/app/selectors/search.selectors';

import { CommonSearchCondition } from 'src/app/models/common-search-condition';
import { User } from 'src/app/models/user';
import { Operator, Subject } from 'src/app/models/common-data';
import { AppEvent } from 'src/app/models/event-log';
import { SearchCondition, SearchConditionWithProblemCount } from 'src/app/models/search-condition';
import { EnglishSearchCondition } from 'src/app/models/english-search-condition';
import { ScienceSearchCondition } from 'src/app/models/science-search-condition';
import { HistorySearchCondition } from 'src/app/models/history-search-condition';
import { NationalLanguageSearchCondition } from 'src/app/models/national-language-search-condition';

import { KEYBOARD_INPUT_DEBOUNCE_TIME, OPERATORS, SEARCH_YEARS, SubjectId } from 'src/app/resources/config';
import { RoutingPathResolver } from '../../../app-routing-path-resolver';
import { QueryParamsMapper } from 'src/app/mappers/query-params-mapper';
import { Log } from 'src/app/utils/log';
import { GeneralError } from 'src/app/errors/general-error';
import { inOut } from 'src/app/resources/animations';

/*** フリーワード検索コンポーネント ***/
@Component({
  selector: 'app-search-free',
  templateUrl: './search-free.component.html',
  styleUrls: ['./search-free.component.scss'],
  animations: [inOut]
})
export class SearchFreeComponent implements OnInit, OnDestroy {
  private LOG_SOURCE = this.constructor.name;

  /** タブメニュー */
  tabMenus = [
    {
      key: 'search',
      label: '条件で探す',
      icon: 'check_box',
      fontSet: 'material-icons-outlined',
      isActive: false,
      disabled: false
    },
    {
      key: 'search-free',
      label: 'フリーワードで探す',
      icon: 'quick_reference_all',
      fontSet: 'material-symbols-outlined',
      isActive: true,
      disabled: false
    }
  ];

  signedInUser$: Observable<User>;

  /*** フォーム値 ***/
  startYearFormControl = new UntypedFormControl();
  endYearFormControl = new UntypedFormControl();
  freewordFormControl = new UntypedFormControl('');
  operatorFormControl = new UntypedFormControl('');
  subjects$: Observable<Subject[]>;
  selectedSubjectId: string;
  defaultSubjectId: string;
  isInvalidEndYear = false;
  selectableYears: number[] = [];
  defaultYear: number;
  operators: Operator[] = [];

  /*** 検索結果数 ***/
  matchedProblemCount = 0;
  matchedProblemCount$: Observable<number>;
  problemCountSearching$: Observable<boolean> = of(false);

  private freewordSubscription: Subscription;
  private matchedProblemCountSubscription: Subscription;

  constructor(private store: Store<RootState>) {}

  ngOnInit(): void {
    this.store.dispatch(initializeProblemCountState());
    this.setUpUser();
    this.setUpSelectableSubjects();
    this.setUpYears();
    this.setUpOperators();
    this.setUpMatchedProblemCount();
    this.setUpFreewordChange();

    /* 検索結果から検索条件に戻った場合の検索条件の設定処理と検索件数の取得処理 */
    this.store
      .select(getSearchCondition)
      .pipe(take(1))
      .subscribe(searchCondition => {
        if (searchCondition)
          setTimeout(() => {
            this.restoreSearchCondition(searchCondition);
            this.findProblemCount();
          });
      });
  }

  ngOnDestroy() {
    if (this.freewordSubscription) this.freewordSubscription.unsubscribe();
    if (this.matchedProblemCountSubscription) this.matchedProblemCountSubscription.unsubscribe();
  }

  onTabMenuClick(key: string) {
    if (key === 'search') {
      this.store.dispatch(navigate({ url: RoutingPathResolver.resolveSearch() }));
    }
  }

  onChangeValues() {
    this.updateFreewordAvailability();

    const [startYear, endYear] = [this.startYearFormControl.value, this.endYearFormControl.value];
    if (this.invalidEndYear(startYear, endYear)) {
      this.isInvalidEndYear = true;
      Log.warn(this.LOG_SOURCE, '入試年度が不正な範囲です');
      this.endYearFormControl.setErrors({ 'min': true });
      return;
    }

    this.isInvalidEndYear = false;
    this.findProblemCount();
  }

  showSearchResultView() {
    const condition = this.collectCondition();

    if (this.matchedProblemCount !== 0) {
      const conditionWithProblemCount: SearchConditionWithProblemCount<any> = {
        ...condition,
        matchedProblemCount: this.matchedProblemCount
      };
      const event: AppEvent = {
        type: 'show-results',
        value: JSON.stringify(conditionWithProblemCount)
      };
      this.store.dispatch(reportAppEvent({ event }));
    }

    const extras: NavigationExtras = {
      queryParams: QueryParamsMapper.encodeSearchCondition(condition)
    };
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveSearchResults(), extras }));
  }

  resetForms() {
    this.store.dispatch(initializeProblemCountState());
    this.selectedSubjectId = this.defaultSubjectId;
    this.startYearFormControl.setValue(this.defaultYear);
    this.endYearFormControl.setValue(this.defaultYear);
    this.freewordFormControl.setValue('');
    this.operatorFormControl.setValue('and');
    this.matchedProblemCount = 0;
    this.updateFreewordAvailability();
  }

  private invalidEndYear(startYear: number, endYear: number): boolean {
    return endYear < startYear;
  }

  /** 推薦・総合科目はフリーワード検索非対応のため、フリーワード入力欄を入力不可にする */
  private updateFreewordAvailability() {
    if (this.selectedSubjectId === SubjectId.SPECIAL_ADMISSIONS) {
      if (this.freewordFormControl.enabled) {
        this.freewordFormControl.setValue('');
        this.freewordFormControl.disable();
      }
    } else if (this.freewordFormControl.disabled) {
      this.freewordFormControl.enable();
    }
  }

  private collectCondition(): CommonSearchCondition {
    const [startYear, endYear] = [this.startYearFormControl.value, this.endYearFormControl.value];
    const operator = this.operatorFormControl.value;
    let freeword = '';
    if (this.freewordFormControl.value !== '') {
      const rawString: string = this.freewordFormControl.value;
      const freewords = rawString.split(/[ 　]+/).filter(it => it !== '');
      if (freewords.length !== 0) freeword = freewords.join(' ');
    }
    const condition: CommonSearchCondition = {
      subjectId: this.selectedSubjectId,
      startYear,
      endYear,
      freeword,
      operator
    };

    Log.debug(this.LOG_SOURCE, '現在の選択状況: ', condition);
    return condition;
  }

  private setUpUser() {
    this.signedInUser$ = this.store.select(getSignedInUser).pipe(filter<User>(it => it != null));
  }

  private setUpSelectableSubjects() {
    const subjects$ = this.store.select(getSubject).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    combineLatest([subjects$, this.signedInUser$])
      .pipe(take(1))
      .subscribe(([subjects, signedInUser]) => {
        if (subjects.length > 0) {
          const _subjects = [] as Subject[];
          subjects.forEach(subject => {
            if (signedInUser.visibleSubjectIds && signedInUser.visibleSubjectIds.includes(subject.id)) {
              // 推薦・総合科目のフリーワードは検索は不可
              if (subject.id === SubjectId.SPECIAL_ADMISSIONS) {
                const editedSubjectName = subject.name + '（非対応）';
                _subjects.push({ ...subject, name: editedSubjectName });
              } else {
                _subjects.push(subject);
              }
            }
          });

          subjects = _subjects;
          this.subjects$ = of(subjects);
          this.defaultSubjectId = subjects[0].id;
          this.selectedSubjectId = subjects[0].id;
          this.updateFreewordAvailability();
        }

        if (subjects.length === 0) {
          this.store.dispatch(
            dispatchAppError({
              source: this.LOG_SOURCE,
              error: GeneralError.unknown(`subject が 0 件です`)
            })
          );
          return;
        }
      });
  }

  private setUpFreewordChange() {
    this.freewordSubscription = this.freewordFormControl.valueChanges.pipe(debounceTime(KEYBOARD_INPUT_DEBOUNCE_TIME)).subscribe(value => {
      if (value) {
        Log.debug(this.LOG_SOURCE, 'フリーワードが入力されました: ', value);
        this.onChangeValues();
      } else {
        this.store.dispatch(initializeProblemCountState());
        this.matchedProblemCount = 0;
      }
    });
  }

  private findProblemCount() {
    const condition = this.collectCondition();
    if (condition.freeword !== '') {
      Log.debug(this.LOG_SOURCE, '結果検索数APIを呼び出します: ', condition);
      this.store.dispatch(findProblemCountByFreeword({ condition, logging: false }));
    }
  }

  private setUpYears() {
    // フリーワード検索の年度は、全科目共通の年度を使用する
    this.selectableYears = [...SEARCH_YEARS].reverse();
    this.defaultYear = [...SEARCH_YEARS].pop();
    this.startYearFormControl.setValue(this.defaultYear);
    this.endYearFormControl.setValue(this.defaultYear);
  }

  private setUpMatchedProblemCount() {
    this.matchedProblemCount$ = this.store.select(getMatchedProblemCount);
    this.problemCountSearching$ = this.store.select(getProblemCountSearching);
    this.matchedProblemCountSubscription = this.matchedProblemCount$.pipe(filter(it => it !== null)).subscribe(value => {
      this.matchedProblemCount = value;
    });
  }

  private setUpOperators() {
    this.operators = OPERATORS;
    this.operatorFormControl.setValue(this.operators[0].id);
  }

  private restoreSearchCondition(
    condition: SearchCondition<EnglishSearchCondition | ScienceSearchCondition | NationalLanguageSearchCondition | HistorySearchCondition>
  ) {
    Log.debug(this.LOG_SOURCE, `検索条件を復元します`, condition);
    if (condition.subjectId) this.selectedSubjectId = condition.subjectId;
    if (condition.startYear) this.startYearFormControl.setValue(condition.startYear);
    if (condition.endYear) this.endYearFormControl.setValue(condition.endYear);
    if (condition.freeword) this.freewordFormControl.setValue(condition.freeword);
    if (condition.operator) this.operatorFormControl.setValue(condition.operator);
    this.updateFreewordAvailability();

    this.store.dispatch(initializeSearchCondition());
  }
}
